import { useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Link } from "@tanstack/react-router";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { createShopCheckout, createGuestShopCheckout } from "@/lib/shop.functions";

interface Props {
  slug: string;
  requiresShipping?: boolean;
  isAuthenticated: boolean;
  returnUrl?: string;
}

export function StripeEmbeddedShopCheckout({ slug, requiresShipping, isAuthenticated, returnUrl }: Props) {
  const [consent, setConsent] = useState({ terms: false, privacy: false, refund: false });
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [proceed, setProceed] = useState(false);

  const consentReady = consent.terms && consent.privacy && consent.refund;
  const guestReady = isAuthenticated || (guestEmail.includes("@") && guestName.trim().length > 0);
  const canProceed = consentReady && guestReady;

  const fetchClientSecret = async (): Promise<string> => {
    const finalReturnUrl = returnUrl ?? `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`;
    const c = { terms: true as const, privacy: true as const, refund: true as const };
    const result = isAuthenticated
      ? await createShopCheckout({ data: { slug, returnUrl: finalReturnUrl, environment: getStripeEnvironment(), consent: c } })
      : await createGuestShopCheckout({ data: { slug, email: guestEmail, name: guestName, returnUrl: finalReturnUrl, environment: getStripeEnvironment(), consent: c } });
    if ("error" in result) throw new Error(result.error);
    if (!result.clientSecret) throw new Error("Stripe did not return a client secret");
    return result.clientSecret;
  };

  if (proceed) {
    return (
      <div id="checkout" className="rounded-lg border hairline bg-obsidian/40 p-2">
        <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    );
  }

  return (
    <div className="rounded-lg border hairline bg-obsidian/40 p-6 space-y-5">
      {!isAuthenticated && (
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Guest checkout</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="text-muted-foreground">Full name</span>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                required
                maxLength={120}
                autoComplete="name"
                className="mt-1 w-full rounded-md border hairline bg-obsidian px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground">Email for receipt</span>
              <input
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                required
                maxLength={255}
                autoComplete="email"
                className="mt-1 w-full rounded-md border hairline bg-obsidian px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">
            After purchase we'll email you a link to claim this order with a free account so your download and rewards stay attached.{" "}
            <Link to="/auth" className="text-gold underline">Or sign in first</Link>.
          </p>
        </div>
      )}

      <fieldset className="space-y-2">
        <legend className="text-xs uppercase tracking-[0.25em] text-gold mb-2">Required consent</legend>
        <ConsentCheckbox
          checked={consent.terms}
          onChange={(v) => setConsent((c) => ({ ...c, terms: v }))}
          label={<>I agree to the <Link to="/terms" className="text-gold underline">Terms of Service</Link>.</>}
        />
        <ConsentCheckbox
          checked={consent.privacy}
          onChange={(v) => setConsent((c) => ({ ...c, privacy: v }))}
          label={<>I agree to the <Link to="/privacy" className="text-gold underline">Privacy Policy</Link>.</>}
        />
        <ConsentCheckbox
          checked={consent.refund}
          onChange={(v) => setConsent((c) => ({ ...c, refund: v }))}
          label={<>I have read and accept the <Link to="/refund" className="text-gold underline">Refund Policy</Link>.</>}
        />
      </fieldset>

      {requiresShipping && (
        <p className="text-[11px] text-muted-foreground">
          Shipping address is collected on the next step. Physical orders ship in 5–14 business days.
        </p>
      )}

      <button
        type="button"
        disabled={!canProceed}
        onClick={() => setProceed(true)}
        className="w-full rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold disabled:cursor-not-allowed disabled:opacity-50"
      >
        Continue to secure payment
      </button>
    </div>
  );
}

function ConsentCheckbox({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode }) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-gold"
      />
      <span className="text-muted-foreground">{label}</span>
    </label>
  );
}
